import { 
  LegalPageLayout, 
  LegalSection, 
  LegalSubsection,
  LegalParagraph,
  LegalList,
  LegalListItem
} from './legal-page-layout';

interface PrivacyPolicyProps {
  onNavigateBack?: () => void;
}

/**
 * Privacy Policy page
 * Structure follows GDPR, CCPA, and enterprise privacy best practices
 * Placeholder content can be replaced by legal team
 */
export function PrivacyPolicy({ onNavigateBack }: PrivacyPolicyProps) {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="11-Feb-2026"
      onNavigateBack={onNavigateBack}
    >
      {/* Introduction */}
      <LegalSection title="1. Introduction">
        <LegalParagraph>
          Upmode AI ("Company", "we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services (the "Service").
        </LegalParagraph>
        <LegalParagraph>
          By using the Service, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use the Service.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add jurisdiction-specific disclosures and scope of coverage]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Information We Collect */}
      <LegalSection title="2. Information We Collect">
        <LegalSubsection title="2.1 Information You Provide">
          <LegalParagraph>
            We collect information you voluntarily provide when using the Service, including:
          </LegalParagraph>
          <LegalList>
            <LegalListItem>Account information (name, email address, company name)</LegalListItem>
            <LegalListItem>Profile and settings preferences</LegalListItem>
            <LegalListItem>Brand data and content you submit for analysis</LegalListItem>
            <LegalListItem>Communications with our support team</LegalListItem>
            <LegalListItem>Payment and billing information</LegalListItem>
          </LegalList>
          <LegalParagraph>
            <em>[Legal team: Add specific data fields and optional vs. required data]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="2.2 Automatically Collected Information">
          <LegalParagraph>
            When you access the Service, we automatically collect certain information, including:
          </LegalParagraph>
          <LegalList>
            <LegalListItem>Device information (IP address, browser type, operating system)</LegalListItem>
            <LegalListItem>Usage data (pages visited, features used, time spent)</LegalListItem>
            <LegalListItem>Log data (access times, errors, performance metrics)</LegalListItem>
            <LegalListItem>Cookies and similar tracking technologies</LegalListItem>
          </LegalList>
          <LegalParagraph>
            <em>[Legal team: Add cookie policy reference and tracking technology details]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="2.3 Information from Third Parties">
          <LegalParagraph>
            We may receive information about you from third parties, such as:
          </LegalParagraph>
          <LegalList>
            <LegalListItem>Authentication providers (if you sign in through third-party services)</LegalListItem>
            <LegalListItem>Payment processors</LegalListItem>
            <LegalListItem>Analytics providers</LegalListItem>
            <LegalListItem>Publicly available sources for brand analysis</LegalListItem>
          </LegalList>
          <LegalParagraph>
            <em>[Legal team: Add specific third-party sources and data types]</em>
          </LegalParagraph>
        </LegalSubsection>
      </LegalSection>

      {/* How We Use Your Information */}
      <LegalSection title="3. How We Use Your Information">
        <LegalParagraph>
          We use the information we collect for the following purposes:
        </LegalParagraph>
        <LegalList>
          <LegalListItem><strong>Provide the Service:</strong> Process your requests, analyze brand perception, and deliver AI-powered insights</LegalListItem>
          <LegalListItem><strong>Account Management:</strong> Create and manage your account, authenticate users, and provide customer support</LegalListItem>
          <LegalListItem><strong>Improve the Service:</strong> Analyze usage patterns, develop new features, and enhance user experience</LegalListItem>
          <LegalListItem><strong>Communications:</strong> Send service updates, security alerts, and respond to inquiries</LegalListItem>
          <LegalListItem><strong>Marketing:</strong> Send promotional materials and product updates (with your consent where required)</LegalListItem>
          <LegalListItem><strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents</LegalListItem>
          <LegalListItem><strong>Compliance:</strong> Comply with legal obligations and enforce our terms</LegalListItem>
        </LegalList>
        <LegalParagraph>
          <em>[Legal team: Add legal bases for processing (GDPR) and purpose limitations]</em>
        </LegalParagraph>
      </LegalSection>

      {/* AI and Data Processing */}
      <LegalSection title="4. AI and Data Processing">
        <LegalParagraph>
          Our Service uses artificial intelligence to analyze brand perception. This involves:
        </LegalParagraph>
        <LegalList>
          <LegalListItem>Processing brand data you submit through AI models</LegalListItem>
          <LegalListItem>Analyzing AI model responses to understand brand representation</LegalListItem>
          <LegalListItem>Generating insights and recommendations based on AI analysis</LegalListItem>
        </LegalList>
        <LegalParagraph>
          Your brand data is processed solely to provide the Service. We do not use your proprietary data to train third-party AI models without your explicit consent.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add AI training policy, model usage terms, and data sharing with AI providers]</em>
        </LegalParagraph>
      </LegalSection>

      {/* How We Share Your Information */}
      <LegalSection title="5. How We Share Your Information">
        <LegalParagraph>
          We may share your information in the following circumstances:
        </LegalParagraph>
        
        <LegalSubsection title="5.1 Service Providers">
          <LegalParagraph>
            We share information with third-party service providers who perform services on our behalf, including:
          </LegalParagraph>
          <LegalList>
            <LegalListItem>Cloud hosting and infrastructure providers</LegalListItem>
            <LegalListItem>Payment processors</LegalListItem>
            <LegalListItem>Analytics and monitoring services</LegalListItem>
            <LegalListItem>Customer support tools</LegalListItem>
          </LegalList>
          <LegalParagraph>
            <em>[Legal team: Add data processing agreements and service provider obligations]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="5.2 Business Transfers">
          <LegalParagraph>
            If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="5.3 Legal Requirements">
          <LegalParagraph>
            We may disclose your information if required by law or in response to valid requests by public authorities.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="5.4 With Your Consent">
          <LegalParagraph>
            We may share your information for other purposes with your explicit consent.
          </LegalParagraph>
        </LegalSubsection>

        <LegalParagraph>
          <em>[Legal team: Add data transfer mechanisms and international transfer safeguards]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Data Security */}
      <LegalSection title="6. Data Security">
        <LegalParagraph>
          We implement appropriate technical and organizational security measures to protect your information, including:
        </LegalParagraph>
        <LegalList>
          <LegalListItem>Encryption of data in transit and at rest</LegalListItem>
          <LegalListItem>Regular security assessments and audits</LegalListItem>
          <LegalListItem>Access controls and authentication mechanisms</LegalListItem>
          <LegalListItem>Employee training on data protection</LegalListItem>
          <LegalListItem>Incident response procedures</LegalListItem>
        </LegalList>
        <LegalParagraph>
          While we strive to protect your information, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add security certifications, compliance frameworks (SOC 2, ISO 27001), and breach notification procedures]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Data Retention */}
      <LegalSection title="7. Data Retention">
        <LegalParagraph>
          We retain your information for as long as necessary to provide the Service and fulfill the purposes described in this policy, unless a longer retention period is required by law.
        </LegalParagraph>
        <LegalParagraph>
          When you close your account, we will delete or anonymize your personal information within [X] days, except where we are required to retain it for legal, regulatory, or security purposes.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add specific retention periods for different data types and legal retention requirements]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Your Rights and Choices */}
      <LegalSection title="8. Your Rights and Choices">
        <LegalParagraph>
          Depending on your location, you may have the following rights regarding your personal information:
        </LegalParagraph>
        
        <LegalSubsection title="8.1 Access and Portability">
          <LegalParagraph>
            You can request access to your personal information and receive a copy in a portable format.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="8.2 Correction and Updates">
          <LegalParagraph>
            You can update or correct your personal information through your account settings or by contacting us.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="8.3 Deletion">
          <LegalParagraph>
            You can request deletion of your personal information, subject to certain exceptions for legal compliance and security.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="8.4 Opt-Out">
          <LegalParagraph>
            You can opt out of marketing communications by following the unsubscribe link in emails or updating your preferences.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="8.5 Additional Rights (GDPR, CCPA)">
          <LegalParagraph>
            If you are in the EEA, UK, or California, you may have additional rights including:
          </LegalParagraph>
          <LegalList>
            <LegalListItem>Right to object to processing</LegalListItem>
            <LegalListItem>Right to restrict processing</LegalListItem>
            <LegalListItem>Right not to be subject to automated decision-making</LegalListItem>
            <LegalListItem>Right to lodge a complaint with a supervisory authority</LegalListItem>
          </LegalList>
        </LegalSubsection>

        <LegalParagraph>
          To exercise these rights, please contact us at privacy@upmode.ai.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add jurisdiction-specific rights, verification procedures, and response timelines]</em>
        </LegalParagraph>
      </LegalSection>

      {/* International Data Transfers */}
      <LegalSection title="9. International Data Transfers">
        <LegalParagraph>
          Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws.
        </LegalParagraph>
        <LegalParagraph>
          When we transfer information internationally, we use appropriate safeguards such as Standard Contractual Clauses approved by the European Commission.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add specific transfer mechanisms, adequacy decisions, and data localization requirements]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Children's Privacy */}
      <LegalSection title="10. Children's Privacy">
        <LegalParagraph>
          The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add age verification measures and parental consent requirements if applicable]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Cookies and Tracking */}
      <LegalSection title="11. Cookies and Tracking Technologies">
        <LegalParagraph>
          We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings.
        </LegalParagraph>
        <LegalParagraph>
          For more information, please see our Cookie Policy.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add detailed cookie policy, types of cookies used, and opt-out mechanisms]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Third-Party Links */}
      <LegalSection title="12. Third-Party Links">
        <LegalParagraph>
          The Service may contain links to third-party websites. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
        </LegalParagraph>
      </LegalSection>

      {/* Changes to This Policy */}
      <LegalSection title="13. Changes to This Privacy Policy">
        <LegalParagraph>
          We may update this Privacy Policy from time to time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes constitutes acceptance of the updated policy.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add notice period and version control]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Contact Information */}
      <LegalSection title="14. Contact Information">
        <LegalParagraph>
          If you have questions about this Privacy Policy or our data practices, please contact us at:
        </LegalParagraph>
        <LegalParagraph>
          <strong>Upmode AI</strong><br />
          Email: privacy@upmode.ai<br />
          <em>[Legal team: Add Data Protection Officer contact, physical address, and EU representative if required]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Regional Disclosures */}
      <LegalSection title="15. Regional-Specific Disclosures">
        <LegalSubsection title="15.1 California Residents (CCPA)">
          <LegalParagraph>
            California residents have specific rights under the California Consumer Privacy Act (CCPA). For more information about these rights and how to exercise them, please contact us.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add CCPA-specific disclosures, categories of data collected, business purposes, and "Do Not Sell" provisions]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="15.2 EEA and UK Residents (GDPR)">
          <LegalParagraph>
            If you are located in the European Economic Area or the United Kingdom, you have additional rights under the General Data Protection Regulation (GDPR).
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add GDPR-specific legal bases, data controller information, and supervisory authority details]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalParagraph>
          <em>[Legal team: Add other regional disclosures as needed (e.g., Brazil LGPD, Canada PIPEDA)]</em>
        </LegalParagraph>
      </LegalSection>
    </LegalPageLayout>
  );
}
